import type { Inputs, Projection } from '../calc/types';
import { formatMoney, formatPercent } from './format';
import { renderChart } from './chart';

/** Matches the three stage headings in the form, in order. */
const STAGE_NAMES = ['Build', 'Coast', 'Top up'] as const;

/** Shown before the user has entered anything worth projecting. */
export function renderEmpty(host: HTMLElement): void {
  const wrap = document.createElement('div');
  wrap.className = 'results__empty';
  const h = document.createElement('h2');
  h.textContent = 'Your projection';
  const p = document.createElement('p');
  p.textContent =
    'Add your current pot or a yearly contribution and the projection appears here — instantly, and without anything leaving your browser.';
  wrap.append(h, p);
  host.replaceChildren(wrap);
}

export function renderInvalid(host: HTMLElement): void {
  const wrap = document.createElement('div');
  wrap.className = 'results__empty';
  const h = document.createElement('h2');
  h.textContent = 'Your projection';
  const p = document.createElement('p');
  p.textContent = 'Fix the highlighted fields and the projection will update.';
  wrap.append(h, p);
  host.replaceChildren(wrap);
}

export function renderResults(host: HTMLElement, input: Inputs, projection: Projection): void {
  const { currency } = input;
  const retirementAge = projection.rows.at(-1)?.age ?? input.retirementAge;

  const wrap = document.createElement('div');

  const heading = document.createElement('h2');
  heading.className = 'results__heading';
  const headingText = document.createElement('span');
  headingText.textContent = `At ${retirementAge}, after ${projection.years} ${projection.years === 1 ? 'year' : 'years'} of saving`;
  const headingIcon = document.createElement('span');
  headingIcon.className = 'corner-emoji';
  headingIcon.setAttribute('aria-hidden', 'true');
  headingIcon.textContent = '💰';
  heading.append(headingText, headingIcon);
  wrap.append(heading);

  // The hero number is the one people actually plan around: yearly income in today's money.
  const hero = document.createElement('div');
  hero.className = 'hero';
  const heroValue = document.createElement('p');
  heroValue.className = 'hero__value';
  heroValue.textContent = formatMoney(projection.incomeReal, currency);
  const heroLabel = document.createElement('p');
  heroLabel.className = 'hero__label';
  heroLabel.textContent = `your yearly spending power (in today’s prices), taking out ${formatPercent(input.withdrawalRate)} per year`;
  hero.append(heroValue, heroLabel);
  wrap.append(hero);

  wrap.append(
    stats([
      ['Pot at retirement', formatMoney(projection.potNominal, currency), 'The cash figure on the statement'],
      ["Pot in today's money", formatMoney(projection.potReal, currency), `Your pot’s spending power after ${formatPercent(input.inflationRate)} inflation a year`],
      ['Income before inflation', formatMoney(projection.incomeNominal, currency), 'The cash income you’ll receive'],
    ]),
  );

  wrap.append(stageSummary(input, projection));

  const chartHost = document.createElement('figure');
  chartHost.className = 'chart';
  const caption = document.createElement('figcaption');
  caption.className = 'chart__caption';
  caption.textContent = 'Pot value by age';
  chartHost.append(caption);
  const plot = document.createElement('div');
  plot.className = 'chart__plot';
  chartHost.append(plot);
  wrap.append(chartHost);

  wrap.append(
    stats([
      ['Paid in', formatMoney(projection.totalContributed, currency), 'Across your contributions'],
      ['Growth', formatMoney(projection.totalGrowth, currency), `at ${formatPercent(input.growthRate)} a year`],
      ['Growth above inflation', formatPercent(projection.realGrowthRate), 'what the pot really gains'],
    ]),
  );

  wrap.append(table(input, projection));
  wrap.append(methodology());

  host.replaceChildren(wrap);
  // The chart measures its host, so it has to be in the document first.
  renderChart(plot, projection, currency);
}

/** A plain-language readout of the 3 resolved stages, so the age ranges from the form are visible alongside the numbers. */
function stageSummary(input: Inputs, projection: Projection): HTMLElement {
  const list = document.createElement('ol');
  list.className = 'stage-summary';
  const last = projection.stages.length - 1;

  projection.stages.forEach((stage, i) => {
    const item = document.createElement('li');
    const name = STAGE_NAMES[i] ?? `Stage ${i + 1}`;
    if (stage.end <= stage.start) {
      item.className = 'stage-summary__item stage-summary__item--skipped';
      item.textContent = `${name}: skipped`;
    } else {
      item.className = 'stage-summary__item';
      const suffix = i === last ? ', until you retire' : '';
      const inflation = stage.growsWithInflation ? ', rising with inflation' : '';
      // Matches the form's wording: the range is half-open, so a dash would
      // read as if the closing age belonged to this stage and the next one.
      item.textContent = `${name} (from age ${stage.start} to ${stage.end}${suffix}): ${formatMoney(stage.annualContribution, input.currency)}/yr${inflation}`;
    }
    list.append(item);
  });

  return list;
}

function stats(items: readonly (readonly [string, string, string])[]): HTMLElement {
  const list = document.createElement('dl');
  list.className = 'stats';
  for (const [label, value, hint] of items) {
    const cell = document.createElement('div');
    cell.className = 'stats__cell';
    const dt = document.createElement('dt');
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.textContent = value;
    const small = document.createElement('p');
    small.className = 'stats__hint';
    small.textContent = hint;
    cell.append(dt, dd, small);
    list.append(cell);
  }
  return list;
}

/** The assumptions behind the figures, sitting with the figures rather than in the page footer. */
function methodology(): HTMLElement {
  const details = document.createElement('details');
  details.className = 'methodology';
  const summary = document.createElement('summary');
  summary.textContent = 'How we work out the numbers';
  details.append(summary);

  const list = document.createElement('ul');
  for (const point of [
    'We assume each contribution is added at the start of the year, so it can benefit from that year’s growth.',
    'Your pot grows once a year using the rate you chose. This figure is assumed to be after fees.',
    'Your estimated yearly income is your chosen withdrawal rate applied to your projected pot.',
    'We project this pot alone. The State Pension, any other savings, and tax on money you withdraw are all left out.',
  ]) {
    const li = document.createElement('li');
    li.textContent = point;
    list.append(li);
  }

  details.append(list);
  return details;
}

/** The accessible companion to the chart: every plotted value, reachable without hovering. */
function table(input: Inputs, projection: Projection): HTMLElement {
  const details = document.createElement('details');
  details.className = 'year-table';
  const summary = document.createElement('summary');
  summary.textContent = 'Year-by-year figures';
  details.append(summary);

  const scroll = document.createElement('div');
  scroll.className = 'year-table__scroll';
  const el = document.createElement('table');

  const head = document.createElement('thead');
  head.append(
    row('th', ['Age', 'Stage', 'Paid in at the start', 'Pot value at the end', "In today's money"]),
  );
  el.append(head);

  /*
   * One row per saving year. A row's own balance is the pot *before* that
   * year's contribution, so the closing figure is carried on the next row —
   * pairing them is what makes the first year read as "paid in £3,000, worth
   * £3,150 by the end" rather than "paid in £3,000, worth nothing".
   *
   * The last entry is the retirement snapshot rather than a year of saving:
   * it supplies the final year's closing balance and gets no row of its own,
   * which would otherwise show a year with nothing paid in.
   */
  const body = document.createElement('tbody');
  projection.rows.forEach((start, i) => {
    const end = projection.rows[i + 1];
    if (!end) return;
    body.append(
      row('td', [
        String(start.age),
        STAGE_NAMES[start.stageIndex] ?? String(start.stageIndex + 1),
        formatMoney(start.contribution, input.currency),
        formatMoney(end.balanceNominal, input.currency),
        formatMoney(end.balanceReal, input.currency),
      ]),
    );
  });
  el.append(body);
  scroll.append(el);
  details.append(scroll);
  return details;
}

function row(cell: 'th' | 'td', values: readonly string[]): HTMLTableRowElement {
  const tr = document.createElement('tr');
  for (const [i, value] of values.entries()) {
    const node = document.createElement(cell);
    if (cell === 'th') node.setAttribute('scope', 'col');
    // Age and stage name read as labels; only the money columns are numeric.
    if (i > 1) node.className = 'num';
    node.textContent = value;
    tr.append(node);
  }
  return tr;
}
